'use client';

import Link from 'next/link';
import React, { useState } from 'react';
import { FaStar, FaMapMarkerAlt, FaClock, FaBus, FaHeart, FaChevronDown, FaChevronUp, FaExternalLinkAlt, FaTicketAlt } from 'react-icons/fa';
import { MdVerified } from 'react-icons/md';
import { ApiRoutes, Routes } from '@/settings/routes';
import { App_text } from '@/settings/AppText';
import QuoteRequestModal from './QuoteRequestModal';
import ImageWithFallback from '@/components/ImageWithFallback';
import { FaCalendarDays, FaCircleChevronRight, FaRegClock, FaWhatsapp } from 'react-icons/fa6';
import { SiTripadvisor } from 'react-icons/si';
import { TbMapPin2 } from 'react-icons/tb';
import Image from 'next/image';

export default function Card({ data = {}, postType, index = 0 }) {
    const [showItinerary, setShowItinerary] = useState(false);
    const packageImages = Array.isArray(data?.images) ? data.images : [];

    const getThumb = () => {
        const thumb = data?.thumbnail;
        const thumbFromImages = packageImages.find((img) => img?.thumbnail) || packageImages[0];

        if (typeof thumb === 'string' && thumb) return thumb.startsWith('http') ? thumb : ApiRoutes.ASSETS_ENDPOINT + thumb;
        if (thumb?.url) return thumb.url.startsWith('http') ? thumb.url : ApiRoutes.ASSETS_ENDPOINT + thumb.url;
        if (thumbFromImages?.url) return thumbFromImages.url.startsWith('http') ? thumbFromImages.url : ApiRoutes.ASSETS_ENDPOINT + thumbFromImages.url;
        return App_text.defaults.images.tour_card;
    };
    const hasOverviewContent = Boolean(data?.includes || data?.itinerary_first);

    return (
        <div className="overflow-hidden bg-white rounded-2xl border border-gray-200" role="article">
            <style>{`
                /* Primary: Orange → Dark */
                .card-btn-primary {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 10px 24px;
                    border-radius: 9999px;
                    border: none;
                    background: linear-gradient(135deg, #E8604C 0%, #313041 100%);
                    color: #ffffff !important;
                    font-size: 0.875rem;
                    font-weight: 600;
                    text-decoration: none;
                    box-shadow: 0 4px 14px rgba(232,96,76,0.35);
                    cursor: pointer;
                    transition: box-shadow 0.25s ease, transform 0.2s ease;
                    white-space: nowrap;
                    width: 100%;
                    height: 42px;
                }
                .card-btn-primary:hover {
                    box-shadow: 0 6px 22px rgba(232,96,76,0.5);
                    transform: translateY(-1px);
                    color: #ffffff !important;
                }

                /* Secondary: Dark → Orange (outline feel via border gradient) */
                .card-btn-secondary {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 10px 24px;
                    border-radius: 9999px;
                    border: none;
                    background: #ffffff;
                    color: #313041;
                    font-size: 0.875rem;
                    font-weight: 600;
                    text-decoration: none;
                    position: relative;
                    cursor: pointer;
                    transition: color 0.25s ease, transform 0.2s ease;
                    white-space: nowrap;
                    width: 100%;
                    height: 42px;
                }
                .card-btn-secondary::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: 9999px;
                    padding: 2px;
                    background: linear-gradient(135deg, #313041, #E8604C);
                    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    -webkit-mask-composite: xor;
                    mask-composite: exclude;
                    pointer-events: none;
                }
                .card-btn-secondary:hover {
                    color: #E8604C;
                    transform: translateY(-1px);
                }
                .card-btn-secondary svg { transition: transform 0.2s ease; }
                .card-btn-secondary:hover svg { transform: translateX(3px); }

                /* Toggle button */
                // .card-btn-toggle {
                //     display: flex;
                //     align-items: center;
                //     justify-content: center;
                //     gap: 8px;
                //     width: 100%;
                //     padding: 10px 16px;
                //     border-radius: 9999px;
                //     border: 1.5px solid #E8604C22;
                //     background: #E8604C08;
                //     color: #E8604C;
                //     font-size: 0.8rem;
                //     font-weight: 700;
                //     letter-spacing: 0.04em;
                //     text-transform: uppercase;
                //     cursor: pointer;
                //     transition: background 0.2s ease, border-color 0.2s ease;
                // }
                // .card-btn-toggle:hover {
                //     background: #E8604C15;
                //     border-color: #E8604C55;
                // }
            `}</style>

            {/* Hero Image Banner */}
            <div className="relative h-[280px] w-full">
                <ImageWithFallback src={getThumb()} alt={data?.title} className="object-cover w-full h-full" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent via-black/40" />

                {/* Private Badge */}
                <div className="absolute z-10 top-5 left-5 flex items-center gap-1.5 px-2.5 py-1.5 bg-white/95 backdrop-blur-sm rounded-lg shadow-md">
                    <span className="text-xs font-bold text-[#E8604C] uppercase tracking-wide">Private</span>
                </div>

                <div className="absolute inset-x-0 bottom-0 p-6 text-white md:text-center">
                    <h3 className="mb-2 text-3xl font-bold leading-tight drop-shadow-lg">{data?.title}</h3>
                    <div className="flex gap-1 items-center md:justify-center">
                        <div className="flex gap-1 items-center">
                            {[...Array(5)].map((_, i) => (
                                <FaStar key={i} className="text-yellow-400 size-4" />
                            ))}
                        </div>
                        <span className="text-sm italic font-semibold">5.0 (125 Reviews)</span>
                        <SiTripadvisor className="text-green-400 size-8" />
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="p-6">
                {/* Description */}
                <div className="mb-6 text-base leading-relaxed text-gray-700 md:text-center" dangerouslySetInnerHTML={{ __html: data?.overview }} />

                <div className="flex flex-col gap-4 mb-6">
                    <div className="p-4 bg-[#f9f9fb] rounded-xl border border-gray-200 space-y-4">
                        {/* Toggle Itinerary */}
                        <button onClick={() => setShowItinerary(!showItinerary)} className="flex gap-2 justify-center items-center w-full text-sm font-semibold card-btn-toggle">
                            View Package Details
                            {showItinerary ? <FaChevronUp className="w-3.5 h-3.5" /> : <FaChevronDown className="w-3.5 h-3.5" />}
                        </button>

                        {/* Itinerary Content */}
                        {showItinerary && <div className="max-w-none text-base rounded prose text-[#202932]" dangerouslySetInnerHTML={{ __html: data?.description }} />}
                    </div>

                    {/* Package Images Grid */}
                    {packageImages.length > 0 && (
                        <div className="flex overflow-x-auto gap-3 scroll-smooth snap-x snap-mandatory">
                            {packageImages.map((img, idx) => (
                                <div
                                    key={img?.id || `img-${idx}`}
                                    className="overflow-hidden relative h-32 rounded-lg group flex-shrink-0 snap-start w-[240px] md:w-[260px] lg:w-[calc((100%-1.5rem)/3)]"
                                >
                                    <ImageWithFallback
                                        src={img.url ? ApiRoutes.ASSETS_ENDPOINT + img.url : App_text.defaults.images.tour_card}
                                        alt={img.title || `Package image ${idx + 1}`}
                                        className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/70" />
                                    <div className="absolute bottom-2 left-2 text-sm font-semibold text-white drop-shadow-lg">{img.title || `Image ${idx + 1}`}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Metadata Icons Row */}
                <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-3">
                    {Array.isArray(data?.destinations) && data.destinations.length > 0 && (
                        <div className="flex gap-2 items-center">
                            <FaMapMarkerAlt className="flex-shrink-0 text-red-500 size-5" />
                            <span className="text-sm font-bold text-custom-blue">{data.destinations[0]?.name}</span>
                        </div>
                    )}
                    {data?.duration && (
                        <div className="flex gap-2 items-center">
                            <FaCalendarDays className="flex-shrink-0 text-red-500 size-5" />
                            <span className="text-sm font-bold text-custom-blue">{data.duration}</span>
                        </div>
                    )}
                    {data?.price && (
                        <div className="flex gap-2 items-center">
                            <FaTicketAlt className="flex-shrink-0 text-red-500 size-5" />
                            <span className="text-sm font-bold text-custom-blue">from €{parseFloat(data.price).toFixed(2)} /per</span>
                        </div>
                    )}
                </div>

                {Array.isArray(data?.destinations) && data.destinations.length > 1 && (
                    <div className="flex gap-2 items-center mb-6 text-sm text-custom-blue">
                        <TbMapPin2 className="flex-shrink-0 text-blue-500 size-5" />
                        <span className="font-bold line-clamp-1">{data.destinations.map((d) => d.name).join(', ')}</span>
                    </div>
                )}

                {typeof data?.destinations === 'string' && data.destinations && (
                    <div className="flex gap-2 items-center mb-6 text-sm text-custom-blue">
                        <TbMapPin2 className="flex-shrink-0 text-blue-500 size-6" />
                        <span className="font-bold line-clamp-1">{data.destinations}</span>
                    </div>
                )}

                {/* CTA Buttons */}
                <div className="flex flex-col gap-3 justify-center items-center mb-2 w-full md:mx-auto md:flex-row md:max-w-xl">
                    <QuoteRequestModal card={data} buttonClassName="card-btn-primary" buttonText={App_text.form_package.button_text} />
                </div>
            </div>
        </div>
    );
}
